import React from 'react';
import Box from '@mui/material/Box';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined';
import WbTwilightOutlinedIcon from '@mui/icons-material/WbTwilightOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooksOutlined';
import { MembershipInformationProducts } from '../../api/model/sponsorship';
import { ProductAccordion } from './ProductAccordion';

type ProductCategoryKey = keyof MembershipInformationProducts;

interface ProductCategoryConfig {
  category: ProductCategoryKey;
  defaultIcon: React.ElementType;
  iconOverrides?: Record<string, React.ElementType>;
  fallbackName: string;
}

const PRODUCT_CATEGORIES: ProductCategoryConfig[] = [
  {
    category: 'primaryCare',
    defaultIcon: HomeOutlinedIcon,
    iconOverrides: {
      NICE_VIRTUAL_PRIMARY_CARE: DevicesOutlinedIcon,
    },
    fallbackName: 'Primary Care',
  },
  {
    category: 'rx',
    defaultIcon: LocalPharmacyOutlinedIcon,
    fallbackName: 'Nice Rx',
  },
  {
    category: 'mentalHealth',
    defaultIcon: WbTwilightOutlinedIcon,
    fallbackName: 'Mental Health',
  },
  {
    category: 'physicalTherapy',
    defaultIcon: FitnessCenterOutlinedIcon,
    fallbackName: 'Physical Therapy',
  },
  {
    category: 'specializedPrograms',
    defaultIcon: LibraryBooksIcon,
    fallbackName: 'Nice Specialized Programs',
  },
];

type ProductAccordionGroupProps = {
  products: MembershipInformationProducts;
  isDarkMode?: boolean;
};

/**
 * Renders a group of `ProductAccordion` components based on the products available in a patient's membership.
 * The component iterates through a predefined list of product categories (`PRODUCT_CATEGORIES`) and displays
 * an accordion for each one, indicating whether the product is included in the patient's plan and what services are included.
 *
 * @param props.products - An object of type `MembershipInformationProducts` containing the product categories and their service inclusions,
 *                         retrieved from the `/v1/membership` API response (under the `products` field).
 * @param props.isDarkMode - A boolean indicating whether the component should be rendered in dark mode.
 */
export function ProductAccordionGroup({ products, isDarkMode = true }: ProductAccordionGroupProps): React.JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '16px' }}>
      {PRODUCT_CATEGORIES.map((config: ProductCategoryConfig) => {
        const product = products[config.category];
        const isIncluded = !!product && Object.keys(product).length > 0;
        const productName = product?.name ?? config.fallbackName;
        const icon = (product?.key ? config.iconOverrides?.[product.key] : undefined) ?? config.defaultIcon;

        return (
          <ProductAccordion
            key={config.category}
            icon={icon}
            productName={productName}
            isIncluded={isIncluded}
            services={product?.services}
            isDarkMode={isDarkMode}
          />
        );
      })}
    </Box>
  );
}

export default ProductAccordionGroup;
